import { cn } from "./cn";

/**
 * Badge de estado (design system §6.4 + mapa de color §2.4).
 *
 * El color nunca es portador único de información: el badge siempre lleva
 * texto. `dot` agrega un punto de color para estados "en vivo".
 *
 * Mapa de tono → dominio (§2.4):
 * - `emerald` Activo / Pagada / OK
 * - `amber`   Pendiente / En proceso / Hold
 * - `rose`    Vencida / Anulada / Crítico
 * - `slate`   Borrador / Inactivo / Cerrado / Sistema (nombre histórico:
 *             pinta con la escala `neutral` del ecosistema)
 * - `brand`   Facturado / Identidad / énfasis de marca
 */
export type BadgeTone =
  | "emerald"
  | "amber"
  | "rose"
  | "slate"
  | "brand";

const TONE_CLS: Record<BadgeTone, string> = {
  emerald: "bg-emerald-50 text-emerald-700",
  amber: "bg-amber-50 text-amber-700",
  rose: "bg-rose-50 text-rose-700",
  slate: "bg-neutral-100 text-neutral-700",
  brand: "bg-brand-50 text-brand-700",
};

const DOT_CLS: Record<BadgeTone, string> = {
  emerald: "bg-emerald-500",
  amber: "bg-amber-500",
  rose: "bg-rose-500",
  slate: "bg-neutral-500",
  brand: "bg-brand-500",
};

interface BadgeProps {
  tone?: BadgeTone | undefined;
  /** Punto de color a la izquierda (estado "en vivo"). */
  dot?: boolean | undefined;
  className?: string | undefined;
  children: React.ReactNode;
}

export function Badge({
  tone = "slate",
  dot = false,
  className,
  children,
}: BadgeProps): JSX.Element {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-semibold",
        TONE_CLS[tone],
        className,
      )}
    >
      {dot && (
        <span
          className={cn("h-1.5 w-1.5 rounded-full", DOT_CLS[tone])}
          aria-hidden="true"
        />
      )}
      {children}
    </span>
  );
}
